import React, { useState, useEffect } from 'react';
import { DYNAMIC_VEHICLE_FRAMEWORKS } from './constants';
import * as Styles from './styles';

export default function SettingsDrawer({
    setShowSettingsDrawer,
    instructorEmail,
    instructorPhone,
    setInstructorPhone,
    hqPostcode,
    setHqPostcode,
    findNearbyAreas,
    nearbyAreas = [],      
    selectedAreas = [],    
    setSelectedAreas,
    travelRadius,
    setTravelRadius,
    vehicleCategories = [], 
    setVehicleCategories,
    triggerMasterConfigurationSave,
    instructorBadgeNumber, 
    setInstructorBadgeNumber,
    instructorId,
    instructorName,       
    postcodeLatitude,     
    postcodeLongitude     
}) {
    const nameParts = (instructorName || "").trim().split(/\s+/);
    const initialTitle = ['Mr', 'Mrs', 'Ms', 'Dr'].includes(nameParts[0]) ? nameParts[0] : 'Mr';
    const initialFirst = ['Mr', 'Mrs', 'Ms', 'Dr'].includes(nameParts[0]) ? (nameParts[1] || '') : (nameParts[0] || '');
    const initialSur = ['Mr', 'Mrs', 'Ms', 'Dr'].includes(nameParts[0]) ? nameParts.slice(2).join(' ') : nameParts.slice(1).join(' ');

    const [title, setTitle] = useState(initialTitle);
    const [firstName, setFirstName] = useState(initialFirst);
    const [surname, setSurname] = useState(initialSur);
    const [newPassword, setNewPassword] = useState('');

    // 📱 Responsive screen width detector for Web vs Mobile styling
    const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);




    const safeSelectedAreas = Array.isArray(selectedAreas) ? selectedAreas : [];
    const safeNearbyAreas = Array.isArray(nearbyAreas) ? nearbyAreas : [];
    const safeVehicleCategories = Array.isArray(vehicleCategories) ? vehicleCategories : [];

    // Dynamic theme variables based on whether it's mobile or web view
    const theme = {
        bg: isMobileScreen ? '#0f172a' : '#ffffff',
        cardBg: isMobileScreen ? '#162238' : '#f8f9fa',
        text: isMobileScreen ? '#ffffff' : '#111111',
        subText: isMobileScreen ? '#94a3b8' : '#4a5568',
        border: isMobileScreen ? 'rgba(255,255,255,0.08)' : '#cbd5e0',
        inputBg: isMobileScreen ? '#162238' : '#ffffff',
        inputBorder: isMobileScreen ? 'rgba(255,255,255,0.15)' : '#cbd5e0',
        inputText: isMobileScreen ? '#ffffff' : '#111111',
        accent: isMobileScreen ? '#60a5fa' : '#0066cc'
    };

    return (
        <div style={Styles.modalOverlay} onClick={() => setShowSettingsDrawer(false)}>
            <div 
                style={{
                    background: theme.bg, 
                    width: isMobileScreen ? '100%' : '460px', 
                    maxWidth: '100%', 
                    height: '100vh',
                    position: 'fixed', 
                    right: 0, 
                    top: 0, 
                    boxSizing: 'border-box', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '18px', 
                    overflowY: 'auto', 
                    padding: '20px', 
                    borderLeft: `1px solid ${theme.border}`, 
                    color: theme.text,
                    boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
                    zIndex: 3000,
                    transition: 'background 0.3s ease'
                }} 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${theme.border}`, paddingBottom: '15px' }}>
                    <h2 style={{ margin: 0, color: theme.accent, fontWeight: '800', fontSize: '18px' }}>⚙️ Profile Settings</h2>
                    <button onClick={() => setShowSettingsDrawer(false)} style={{ background: 'none', border: 'none', color: theme.subText, fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                </div>

                {/* 👤 PERSONAL DETAILS */}
                <div style={Styles.flexCol}>
                    <h3 style={{ margin: '5px 0 10px 0', color: theme.accent, fontSize: '14px', fontWeight: '800' }}>👤 Personal Details</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '8px' }}>
                        <div>
                            <label style={{ ...labelStyle, color: theme.subText }}>Title</label>
                            <select value={title} onChange={e => setTitle(e.target.value)} style={{ ...inputStyle(theme) }}>
                                <option style={{ background: theme.inputBg }}>Mr</option>
                                <option style={{ background: theme.inputBg }}>Mrs</option>
                                <option style={{ background: theme.inputBg }}>Ms</option>
                                <option style={{ background: theme.inputBg }}>Dr</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ ...labelStyle, color: theme.subText }}>First Name</label>
                            <input value={firstName} onChange={e => setFirstName(e.target.value)} style={{ ...inputStyle(theme) }} />
                        </div>
                        <div>
                            <label style={{ ...labelStyle, color: theme.subText }}>Surname</label>
                            <input value={surname} onChange={e => setSurname(e.target.value)} style={{ ...inputStyle(theme) }} />
                        </div>
                    </div>
                </div>

                {/* 🔒 SECURITY & PASSWORD */}
                <div style={{ background: theme.cardBg, padding: '15px', borderRadius: '12px', border: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <h3 style={{ margin: '0 0 5px 0', color: theme.accent, fontSize: '14px', fontWeight: '800' }}>🔒 Security & Password</h3>
                    <label style={{ fontSize: '11px', color: theme.subText, fontWeight: 'bold', textTransform: 'uppercase' }}>New Password (Leave blank to keep current)</label>
                    <input 
                        type="password" 
                        value={newPassword} 
                        onChange={e => setNewPassword(e.target.value)} 
                        placeholder="At least 6 characters" 
                        style={{ ...inputStyle(theme) }} 
                    />
                </div>

                {/* CONTACT */}
                <div style={Styles.flexCol}>
                    <h3 style={{ margin: '5px 0 10px 0', color: theme.accent, fontSize: '14px', fontWeight: '800' }}>📞 Contact Verification</h3>
                    <label style={{ ...labelStyle, color: theme.subText }}>Public Email Address (Locked)</label>
                    <input value={instructorEmail} readOnly style={{ ...inputStyle(theme), opacity: 0.6, cursor: 'not-allowed', backgroundColor: isMobileScreen ? '#090d16' : '#f1f5f9' }} />
                    <label style={{ ...labelStyle, color: theme.subText, marginTop: '8px' }}>Mobile Contact Phone Line</label>
                    <input value={instructorPhone} onChange={e => setInstructorPhone(e.target.value)} style={{ ...inputStyle(theme) }} />
                </div>

                {/* 🛡 DVSA BADGE RECORD */}
                <div style={{ background: theme.cardBg, padding: '15px', borderRadius: '12px', border: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <h3 style={{ margin: '0 0 5px 0', color: theme.accent, fontSize: '14px', fontWeight: '800' }}>🛡️ DVSA Badge Record</h3>
                    <label style={{ fontSize: '11px', color: theme.subText, fontWeight: 'bold', textTransform: 'uppercase' }}>DVSA Badge Number</label>
                    <input 
                        type="text"
                        value={instructorBadgeNumber || ''} 
                        onChange={e => setInstructorBadgeNumber(e.target.value)} 
                        placeholder="Enter badge number..."
                        style={{ ...inputStyle(theme) }} 
                    />
                </div>

                {/* OPERATING RADIUS & AREA PICKER */}
                <div style={Styles.flexCol}>
                    <h3 style={{ margin: '15px 0 10px 0', color: '#34d399', fontSize: '14px', fontWeight: '800' }}>📍 Coverage Zones & Postcode</h3>
                    
                    <label style={{ ...labelStyle, color: theme.subText }}>HQ Postcode</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input 
                            value={hqPostcode || ''} 
                            onChange={e => setHqPostcode(e.target.value)} 
                            placeholder="E.g., SW1A 1AA" 
                            style={{ ...inputStyle(theme), textTransform: 'uppercase' }} 
                        />
                        <button 
                            type="button" 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                findNearbyAreas(hqPostcode, travelRadius);
                            }} 
                            style={{ backgroundColor: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', padding: '0 16px', cursor: 'pointer', fontSize: '12px' }}
                        >
                            Find
                        </button>
                    </div>

                    {/* Area Checklist */}
                    <div style={{ marginTop: '12px' }}>
                        <label style={{ ...labelStyle, display: 'block', marginBottom: '8px', color: theme.subText }}>Selected Areas:</label>
                        
                        <div 
                            style={{ 
                                display: 'flex', flexWrap: 'wrap', gap: '8px', 
                                background: theme.cardBg, padding: '12px', borderRadius: '12px',
                                border: `1px solid ${theme.border}`, minHeight: '40px'
                            }}
                        >
{(() => {
                                // 🔑 Ensure all user-selected areas are always combined with discovered nearby areas so they never vanish
                                const combinedAreas = Array.from(new Set([...safeSelectedAreas, ...safeNearbyAreas]))
                                    .filter(area => area && typeof area === 'string' && area.trim() !== '')
                                    .sort();
                                
                                if (combinedAreas.length === 0) {
                                    return <div style={{ color: theme.subText, fontStyle: 'italic', fontSize: '12px', padding: '5px' }}>Enter postcode to discover coverage zones...</div>;
                                }

                                return combinedAreas.map((area) => {
                                    const isSelected = safeSelectedAreas.includes(area);
                                    return (
                                        <button
                                            type="button"
                                            key={area}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation(); 
                                                setSelectedAreas(prev => {
                                                    const safePrev = Array.isArray(prev) ? prev : [];
                                                    return isSelected ? safePrev.filter(a => a !== area) : [...safePrev, area];
                                                });
                                            }}
                                            style={{
                                                padding: '6px 12px', borderRadius: '20px',
                                                border: `1px solid ${isSelected ? '#3b82f6' : theme.border}`,
                                                background: isSelected ? '#2563eb' : (isMobileScreen ? '#0f172a' : '#ffffff'),
                                                color: isSelected ? '#ffffff' : (isMobileScreen ? '#ffffff' : '#111111'),
                                                cursor: 'pointer', fontSize: '12px', fontWeight: '600', transition: 'all 0.2s'
                                            }}
                                        >
                                            {isSelected ? '✓ ' : '+ '} {area}
                                        </button>
                                    );
                                });
                            })()}

                        </div>
                    </div>

                    {/* Radius Slider */}
                    <label style={{ fontSize: '12px', color: theme.subText, fontWeight: 'bold', marginTop: '12px', display: 'block' }}>
                        Radius: <span style={{ color: theme.accent }}>{travelRadius} Miles</span>
                    </label>
                    <input 
                        type="range" 
                        min="1" 
                        max="10" 
                        value={travelRadius} 
                        onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            setTravelRadius(val);
                            findNearbyAreas(hqPostcode, val); 
                        }} 
                        style={{ width: '100%', cursor: 'pointer', accentColor: '#3b82f6', marginTop: '4px' }} 
                    />
                </div>

                {/* VEHICLE TOGGLES */}
                <div style={Styles.flexCol}>
                    <h3 style={{ margin: '15px 0 10px 0', color: '#fbbf24', fontSize: '14px', fontWeight: '800' }}>🚗 Active Vehicle Class Toggles</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {Object.keys(DYNAMIC_VEHICLE_FRAMEWORKS).map(framework => {
                            const isChecked = safeVehicleCategories.includes(framework);
                            return (
                                <label key={framework} style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: theme.cardBg, padding: '10px 12px', borderRadius: '10px', border: `1px solid ${theme.border}`, color: theme.text, fontWeight: '500' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={isChecked} 
                                        onChange={() => setVehicleCategories(prev => {
                                            const safePrev = Array.isArray(prev) ? prev : [];
                                            return isChecked ? safePrev.filter(f => f !== framework) : [...safePrev, framework];
                                        })} 
                                        style={{ accentColor: '#3b82f6', width: '16px', height: '16px' }} 
                                    />
                                    {framework}
                                </label>
                            );
                        })}
                    </div>
                </div>

<button 
    type="button"
    onClick={(e) => { 
        e.preventDefault(); 
        e.stopPropagation(); 
        triggerMasterConfigurationSave({
            name: `${title} ${firstName} ${surname}`.trim(),
            newPassword: newPassword.trim() !== '' ? newPassword : null,
            latitude: postcodeLatitude,   
            longitude: postcodeLongitude,
            // 🔑 Ensure these are explicitly passed to the save function!
            selected_areas: safeSelectedAreas,
            transmission_type: safeVehicleCategories,
postcode: hqPostcode
        }); 
    }} 
    style={{ width: '100%', padding: '14px', background: '#059669', color: '#ffffff', borderRadius: '12px', marginTop: '10px', marginBottom: '30px', fontSize: '14px', border: 'none', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)' }}
>
    💾 Save Profile & Coverage Settings
</button>
            </div>
        </div>
    );
}

const labelStyle = { fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '5px' };

const inputStyle = (theme) => ({
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    background: theme.inputBg,
    color: theme.inputText,
    fontSize: '13px',
    boxSizing: 'border-box'
});